import type { StatName } from './deeds.js';

/**
 * Шмот и сеты. Предметы дают плоские прибавки, сеты — процентные множители
 * к механикам. Палёные бренды дают долю от бонуса и рискуют опозорить.
 */

export const GEAR_SLOTS = ['kostyum', 'obuv', 'cep', 'persten', 'barsetka', 'voda', 'partak'] as const;
export type GearSlot = (typeof GEAR_SLOTS)[number];

export interface ItemSpec {
  def: string;
  title: string;
  slot: GearSlot;
  price: number;
  stats?: Partial<Record<StatName, number>>;
  /** Доля бонуса: у палёнки 0.6 от оригинала. */
  quality?: number;
  /** Штраф к Чоткости за палёнку. */
  chotkostPenalty?: number;
  /** Шанс опозориться на проверке Чоткости. */
  shameChance?: number;
  set?: string;
}

export interface SetSpec {
  def: string;
  title: string;
  items: string[];
  /** Множители к механикам: меньше единицы — быстрее и тише. */
  timeFactor?: number;
  walkFactor?: number;
  heatFactor?: number;
  sellFactor?: number;
  /** Процентные прибавки к статам. */
  statPercent?: Partial<Record<StatName, number>>;
  /** Скидка к стрессу за дело. */
  stressRelief?: number;
  /** Скидка комиссии приёмки в долях. */
  commissionRelief?: number;
}

export interface GearEffects {
  statBonus: Partial<Record<StatName, number>>;
  statPercent: Partial<Record<StatName, number>>;
  timeFactor: number;
  walkFactor: number;
  heatFactor: number;
  sellFactor: number;
  commissionRelief: number;
  stressRelief: number;
  shameChance: number;
  /** Собранные сеты — их показываем игроку. */
  sets: SetSpec[];
}

export const NO_GEAR: GearEffects = {
  statBonus: {},
  statPercent: {},
  timeFactor: 1,
  walkFactor: 1,
  heatFactor: 1,
  sellFactor: 1,
  commissionRelief: 0,
  stressRelief: 0,
  shameChance: 0,
  sets: [],
};

/** Собран ли сет полностью надетым шмотом. */
export function setCollected(set: SetSpec, worn: readonly ItemSpec[]): boolean {
  const defs = new Set(worn.map((item) => item.def));
  return set.items.every((def) => defs.has(def));
}

/**
 * Итоговые эффекты: плоские прибавки предметов плюс множители собранных сетов.
 * Палёнка режет свою прибавку и добавляет шанс позора.
 */
export function combineGear(worn: readonly ItemSpec[], sets: readonly SetSpec[]): GearEffects {
  const effects: GearEffects = {
    ...NO_GEAR,
    statBonus: {},
    statPercent: {},
    sets: [],
  };

  for (const item of worn) {
    const quality = item.quality ?? 1;
    for (const [stat, value] of Object.entries(item.stats ?? {})) {
      const key = stat as StatName;
      effects.statBonus[key] = (effects.statBonus[key] ?? 0) + Math.round((value ?? 0) * quality);
    }
    if (item.chotkostPenalty) {
      effects.statBonus.chotkost = (effects.statBonus.chotkost ?? 0) - item.chotkostPenalty;
    }
    // Шансы позора складываются: два палёных бренда заметнее одного.
    effects.shameChance = Math.min(0.5, effects.shameChance + (item.shameChance ?? 0));
  }

  for (const set of sets) {
    if (!setCollected(set, worn)) continue;
    effects.sets.push(set);
    effects.timeFactor *= set.timeFactor ?? 1;
    effects.walkFactor *= set.walkFactor ?? 1;
    effects.heatFactor *= set.heatFactor ?? 1;
    effects.sellFactor *= set.sellFactor ?? 1;
    effects.commissionRelief += set.commissionRelief ?? 0;
    effects.stressRelief += set.stressRelief ?? 0;
    for (const [stat, percent] of Object.entries(set.statPercent ?? {})) {
      const key = stat as StatName;
      effects.statPercent[key] = (effects.statPercent[key] ?? 0) + (percent ?? 0);
    }
  }

  return effects;
}

/** Стат с учётом плоских прибавок и процентов от сетов. */
export function statWithGear(base: number, stat: StatName, gear: GearEffects): number {
  const flat = base + (gear.statBonus[stat] ?? 0);
  const percent = gear.statPercent[stat] ?? 0;
  return Math.max(0, Math.round(flat * (1 + percent / 100)));
}
